import { type LoaderFunctionArgs, useLoaderData } from "react-router";

import AssessmentApi from "@/api/assessment-api";
import CandidateApi from "@/api/candidate-api";
import CommentApi from "@/api/comment-api";
import CandidateInfo from "@/components/candidate-info";
import CommentSection from "@/components/comment-section";
import ApiErrorComponent from "@/components/common/api-error";
import ScoreCard from "@/components/scorecard";
import { usePageTitle } from "@/hooks/title";
import type ApiError from "@/models/ApiError";
import type { Assignment } from "@/types/assignment";
import type { Candidate } from "@/types/candidate";
import type { Comment } from "@/types/comment";
import type { Scorecard } from "@/types/scorecard";

export default function CandidatePage() {
  const {
    candidate,
    assignment,
    comments,
    assessment,
    error: { c_error, co_error, sc_error },
  } = useLoaderData() as LoaderData;

  // Hooks run before any early return; usePageTitle falls back to the plain
  // app title when the candidate failed to load.
  usePageTitle(candidate?.name);

  if (c_error || co_error || sc_error) {
    return <ApiErrorComponent error={c_error || co_error || sc_error} />;
  }
  if (!candidate || !assignment || !assessment) {
    return <ApiErrorComponent error="error/candidate-not-loaded" />;
  }

  const hide_comments =
    assignment.user_role === "assessor" && !assignment.settings?.sharing_settings?.comments;

  return (
    <div key={candidate.id} className="mx-auto max-w-[60rem]">
      <CandidateInfo full candidate={candidate} assessment={assessment} />
      {!Object.keys(assessment).includes("detail") && (
        <ScoreCard assessment={assessment} competencies={assessment.competencies} />
      )}
      {!hide_comments && <CommentSection comments={comments ?? []} candidate={candidate} />}
    </div>
  );
}

type LoaderData = {
  candidate: Candidate | null;
  assignment: Assignment | null;
  comments: Comment[] | null;
  assessment: Scorecard | null;
  error: {
    c_error: ApiError | null;
    co_error: ApiError | null;
    sc_error: ApiError | null;
  };
};

export async function loader({
  request,
  params: { assignment_id, candidate_id },
}: LoaderFunctionArgs): Promise<LoaderData> {
  if (!candidate_id || !assignment_id) {
    throw new Error("Missing candidate_id or assignment_id");
  }
  const [
    { data: candidate_data, error: c_error },
    { data: comments, error: co_error },
    { data: assessment, error: sc_error },
  ] = await Promise.all([
    CandidateApi.fetchOne(candidate_id, assignment_id, request.signal),
    CommentApi.fetchCandidateComments(candidate_id, request.signal),
    AssessmentApi.getCandidateScorecards(candidate_id, request.signal),
  ]);
  return {
    candidate: candidate_data?.candidate ?? null,
    comments,
    assignment: candidate_data?.assignment ?? null,
    assessment,
    error: { c_error, co_error, sc_error },
  };
}
